'use client';

import { cloneElement, isValidElement, lazy, Suspense, useEffect, useMemo } from 'react';
import type { Components } from 'react-markdown';
import type { StreamdownAnchorTransform } from '@/components/markdown/StreamdownRenderer';
import { StreamdownRenderer } from '@/components/markdown/StreamdownRenderer';
import { extractTweetIdFromUrl, extractUsernameFromUrl } from '@/lib/twitter/url';

export type StreamdownComponents = Components & {
  /**
   * Allow downstream feature teams to register custom block renderers (e.g., tweet-examples)
   * without tripping the narrower react-markdown Components index signature.
   */
  [customComponent: string]: Components[keyof Components] | undefined;
};

interface MarkdownProviderProperties {
  children: string;
  className?: string | undefined;
  components?: StreamdownComponents | undefined;
  enablePreviews?: boolean | undefined;
  transformAnchor?: StreamdownAnchorTransform | undefined;
}

const LazyTweetPreview = lazy(async () => {
  const { TweetPreview } = await import('@/components/shared/previews/TweetPreview');
  return { default: TweetPreview };
});

const LazyUserPreview = lazy(async () => {
  const { UserPreview } = await import('@/components/shared/previews/UserPreview');
  return { default: UserPreview };
});

type AnchorTransformContext = Parameters<StreamdownAnchorTransform>[0];

const buildPreviewAnchorTransform = (): StreamdownAnchorTransform => {
  function previewTransform({ url, renderAnchor, anchorChildren }: AnchorTransformContext): React.ReactNode {
    const tweetId = extractTweetIdFromUrl(url);
    if (tweetId) {
      const previewAnchor = renderAnchor({ target: '_blank', rel: 'noopener noreferrer' });
      const renderNormalizedAnchor = () => {
        if (!isValidElement<{ href?: string }>(previewAnchor)) {
          return previewAnchor;
        }
        return cloneElement(previewAnchor, {
          href: `https://x.com/i/status/${tweetId}`,
        });
      };
      return (
        <Suspense fallback={renderNormalizedAnchor()}>
          <LazyTweetPreview tweetId={tweetId} allowNestedHover>
            {renderNormalizedAnchor()}
          </LazyTweetPreview>
        </Suspense>
      );
    }

    const username = extractUsernameFromUrl(url);
    if (username) {
      const previewAnchor = renderAnchor({ target: '_blank', rel: 'noopener noreferrer' });
      return (
        <Suspense fallback={previewAnchor}>
          <LazyUserPreview username={username} allowNestedHover>
            {previewAnchor}
          </LazyUserPreview>
        </Suspense>
      );
    }

    return anchorChildren;
  }

  return previewTransform;
};

const mergeAnchorTransforms = (
  primary?: StreamdownAnchorTransform,
  fallback?: StreamdownAnchorTransform
): StreamdownAnchorTransform | undefined => {
  if (!primary) {
    return fallback;
  }
  if (!fallback) {
    return primary;
  }
  // eslint-disable-next-line @typescript-eslint/promise-function-async -- Transform returns React nodes synchronously
  const composedTransform: StreamdownAnchorTransform = (context) => {
    const primaryResult = primary(context);
    if (primaryResult !== undefined && primaryResult !== null) {
      return primaryResult;
    }
    return fallback(context);
  };
  return composedTransform;
};

export function StreamdownProvider({
  children,
  className,
  components,
  enablePreviews = false,
  transformAnchor,
}: MarkdownProviderProperties) {
  useEffect(() => {
    if (!enablePreviews) {
      return;
    }
    Promise.all([
      import('@/components/shared/previews/TweetPreview'),
      import('@/components/shared/previews/UserPreview'),
    ]).catch(() => {
      // ignore preload failures
    });
  }, [enablePreviews]);

  const previewTransform = useMemo<StreamdownAnchorTransform | undefined>(
    () => (enablePreviews ? buildPreviewAnchorTransform() : undefined),
    [enablePreviews]
  );

  const combinedTransform = useMemo<StreamdownAnchorTransform | undefined>(
    () => mergeAnchorTransforms(previewTransform, transformAnchor),
    [previewTransform, transformAnchor]
  );

  return (
    <StreamdownRenderer className={className} components={components} transformAnchor={combinedTransform}>
      {children}
    </StreamdownRenderer>
  );
}
